/* eslint-disable react-refresh/only-export-components -- Context files legitimately mix component and non-component exports */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import {
  BuiltInFilterState,
  SortDirection,
  ViewType,
} from "../types";
import { parseScheduledFilterParam, serializeScheduledFilters } from "../lib/scheduledFilters";
import {
  getInitialPersistedToggles,
  useViewStatePersistence,
} from "../hooks/useViewStatePersistence";

// ── What lives here, and what doesn't ────────────────────────────────────────
//
// This context owns **view state**: what the user is currently looking at and
// how it is arranged. Three stores touch that area and each keeps a distinct
// job:
//
//   • `ViewContext` (this file) — the runtime owner. The active view derived
//     from the route, the filter state parsed out of the URL, the search term,
//     the sort directions and the visibility toggles.
//   • `useViewStatePersistence` — the storage layer. It reads the saved
//     snapshot on first mount and mirrors changes to localStorage. This
//     provider is its single call site, so the state and its persistence sit
//     together.
//   • `UIPreferencesContext` — per-user, server-synced *preferences* (which
//     chips a task card shows). Different lifetime (an account, not a tab) and
//     different scope (card chrome, not the view), so it stays separate.
//
// The URL remains authoritative for the route and the filters: this context
// derives them from the router and writes back through it rather than keeping
// a second copy.

export interface ViewContextValue {
  /** Which top-level view is on screen, derived from the route. */
  viewType: ViewType;

  // ── Filters ────────────────────────────────────────────────────────────────
  /** Id of the active saved filter, or undefined when none is applied. */
  activeFilterId: string | undefined;
  /**
   * Applies a saved filter, replacing any built-in selections. Pass null to
   * clear every filter.
   */
  setActiveFilterId: (filterId: string | null) => void;
  /** Built-in (ad-hoc) filter selections, parsed from the URL. */
  builtInFilterState: BuiltInFilterState;
  /** True when any built-in filter is narrowing the board. */
  hasBuiltInFilters: boolean;
  /** Writes a new built-in filter selection back to the URL. */
  setBuiltInFilterState: (state: BuiltInFilterState) => void;
  /** Turns off the saved filter and every built-in filter at once. */
  clearFilters: () => void;

  // ── Search ─────────────────────────────────────────────────────────────────
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;

  // ── Sort ───────────────────────────────────────────────────────────────────
  // The two sorts are mutually exclusive: cycling one (off → asc → desc → off)
  // turns the other off.
  sortByPriority: SortDirection;
  cycleSortByPriority: () => void;
  sortByCreatedDate: SortDirection;
  cycleSortByCreatedDate: () => void;

  // ── Visibility toggles ─────────────────────────────────────────────────────
  showCompleted: boolean;
  toggleShowCompleted: () => void;
  showArchived: boolean;
  toggleShowArchived: () => void;
  allSubtasksExpanded: boolean;
  toggleAllSubtasksExpanded: () => void;
}

function cycleSortDirection(current: SortDirection): SortDirection {
  if (current === "off") return "asc";
  if (current === "asc") return "desc";
  return "off";
}

const BUILT_IN_SEARCH_KEYS = [
  "priorities",
  "lists",
  "tags",
  "scheduled",
  "work_hours",
] as const;

function pickBuiltInSearch(
  search: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    BUILT_IN_SEARCH_KEYS.map((key) => [key, search[key]]),
  );
}

const ViewContext = createContext<ViewContextValue | null>(null);

export function ViewProvider({ children }: { children: ReactNode }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigate = useNavigate() as any;
  const { pathname } = useLocation();
  const { taskId } = useParams({ strict: false }) as { taskId?: string };
  const {
    filter: activeFilterId,
    priorities: prioritiesParam,
    lists: listsParam,
    tags: tagsParam,
    scheduled: scheduledParam,
    work_hours: workHoursParam,
    view: viewParam,
  } = useSearch({ strict: false }) as {
    filter?: string;
    priorities?: string;
    lists?: string;
    tags?: string;
    scheduled?: string;
    work_hours?: string;
    view?: string;
  };

  // While a task dialog is open (`/task/$taskId`), keep the underlying
  // top-level view mounted instead of falling through to the board. The
  // originating view is encoded as `?view=calendar|emails` when the dialog
  // is opened from those routes.
  const viewType: ViewType =
    pathname === "/calendar" || (taskId && viewParam === "calendar")
      ? "calendar"
      : pathname === "/emails" || (taskId && viewParam === "emails")
        ? "emails"
        : pathname === "/captures"
          ? "captures"
          : "board";

  // Parse built-in filter state from URL params
  const builtInFilterState: BuiltInFilterState = useMemo(
    () => ({
      priorities: prioritiesParam
        ? prioritiesParam
            .split(",")
            .map(Number)
            .filter((n) => !isNaN(n))
        : [],
      listIds: listsParam
        ? listsParam
            .split(",")
            .filter(Boolean)
            .map((id) => (id === "none" ? null : id))
        : [],
      tagIds: tagsParam ? tagsParam.split(",").filter(Boolean) : [],
      scheduledFilter: parseScheduledFilterParam(scheduledParam),
      workHoursFilter:
        workHoursParam === "only" || workHoursParam === "exclude"
          ? workHoursParam
          : null,
    }),
    [prioritiesParam, listsParam, tagsParam, scheduledParam, workHoursParam],
  );

  const hasBuiltInFilters =
    builtInFilterState.priorities.length > 0 ||
    builtInFilterState.listIds.length > 0 ||
    builtInFilterState.tagIds.length > 0 ||
    builtInFilterState.scheduledFilter.length > 0 ||
    !!builtInFilterState.workHoursFilter;

  const [searchTerm, setSearchTerm] = useState("");

  // One read of the saved snapshot per mount, shared by the five toggle/sort
  // initializers below. Held in state (rather than a plain `const`) so the
  // read stays lazy and happens once on mount instead of on every render —
  // this provider sits on the navigation path, and each read parses the whole
  // localStorage entry.
  const [initialToggles] = useState(getInitialPersistedToggles);

  const [showCompleted, setShowCompleted] = useState(
    initialToggles.showCompleted,
  );
  // Archived view: when true, the board shows ONLY archived tasks so the
  // user can browse them, restore them, or delete them for good. When
  // false (the default), archived tasks are hidden everywhere.
  const [showArchived, setShowArchived] = useState(initialToggles.showArchived);
  const [allSubtasksExpanded, setAllSubtasksExpanded] = useState(
    initialToggles.allSubtasksExpanded,
  );
  const [sortByPriority, setSortByPriority] = useState<SortDirection>(
    initialToggles.sortByPriority,
  );
  const [sortByCreatedDate, setSortByCreatedDate] = useState<SortDirection>(
    initialToggles.sortByCreatedDate,
  );

  useViewStatePersistence({
    filter: activeFilterId,
    priorities: prioritiesParam,
    lists: listsParam,
    tags: tagsParam,
    scheduled: scheduledParam,
    work_hours: workHoursParam,
    sortByPriority,
    sortByCreatedDate,
    showCompleted,
    showArchived,
    allSubtasksExpanded,
  });

  // A saved filter and the built-in filters stack (#744): each setter writes
  // its own keys and carries the other kind's keys over from the current
  // search. Anything else in the search (an open email, a subtask) is dropped,
  // as before.
  const setActiveFilterId = useCallback(
    (filterId: string | null) => {
      navigate({
        search: (prev: Record<string, unknown>) =>
          ({
            ...pickBuiltInSearch(prev),
            filter: filterId ?? undefined,
          }) as Record<string, unknown>,
        replace: true,
      });
    },
    [navigate],
  );

  const cycleSortByPriority = useCallback(() => {
    setSortByPriority(cycleSortDirection);
    setSortByCreatedDate("off");
  }, []);

  const cycleSortByCreatedDate = useCallback(() => {
    setSortByCreatedDate(cycleSortDirection);
    setSortByPriority("off");
  }, []);

  const toggleShowCompleted = useCallback(
    () => setShowCompleted((prev) => !prev),
    [],
  );
  const toggleShowArchived = useCallback(
    () => setShowArchived((prev) => !prev),
    [],
  );
  const toggleAllSubtasksExpanded = useCallback(
    () => setAllSubtasksExpanded((prev) => !prev),
    [],
  );

  const setBuiltInFilterState = useCallback(
    (state: BuiltInFilterState) => {
      const params: Record<string, string | undefined> = {};
      params.priorities =
        state.priorities.length > 0 ? state.priorities.join(",") : undefined;
      // "No list" is a null id; the URL spells it "none" (see the parser above).
      params.lists =
        state.listIds.length > 0
          ? state.listIds.map((id) => id ?? "none").join(",")
          : undefined;
      params.tags =
        state.tagIds.length > 0 ? state.tagIds.join(",") : undefined;
      params.scheduled = serializeScheduledFilters(state.scheduledFilter);
      params.work_hours = state.workHoursFilter ?? undefined;
      navigate({
        search: (prev: Record<string, unknown>) =>
          ({ filter: prev.filter, ...params }) as Record<string, unknown>,
        replace: true,
      });
    },
    [navigate],
  );

  const clearFilters = useCallback(() => {
    navigate({ search: () => ({}) as Record<string, unknown>, replace: true });
  }, [navigate]);

  const value = useMemo<ViewContextValue>(
    () => ({
      viewType,
      activeFilterId,
      setActiveFilterId,
      builtInFilterState,
      hasBuiltInFilters,
      setBuiltInFilterState,
      clearFilters,
      searchTerm,
      setSearchTerm,
      sortByPriority,
      cycleSortByPriority,
      sortByCreatedDate,
      cycleSortByCreatedDate,
      showCompleted,
      toggleShowCompleted,
      showArchived,
      toggleShowArchived,
      allSubtasksExpanded,
      toggleAllSubtasksExpanded,
    }),
    [
      viewType,
      activeFilterId,
      setActiveFilterId,
      builtInFilterState,
      hasBuiltInFilters,
      setBuiltInFilterState,
      clearFilters,
      searchTerm,
      sortByPriority,
      cycleSortByPriority,
      sortByCreatedDate,
      cycleSortByCreatedDate,
      showCompleted,
      toggleShowCompleted,
      showArchived,
      toggleShowArchived,
      allSubtasksExpanded,
      toggleAllSubtasksExpanded,
    ],
  );

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

export function useView(): ViewContextValue {
  const ctx = useContext(ViewContext);
  if (!ctx) throw new Error("useView must be used inside ViewProvider");
  return ctx;
}
